import type { QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

export interface RuleState {
  targetsOfVisibleQuestions: Array<{
    questionId: number;
    itemId: number;
    targetId: number;
    externalId: string | null;
    isTargeted: boolean;
  }>;
  targetsOfVisibleGroups: Array<{ groupId: number; isTargeted: boolean }>;
  targetsOfDisabledItems: Array<{ itemId: number; isTargeted: boolean }>;
  isQuestionVisible: (questionId: number, externalId: string | null) => boolean;
  isGroupVisible: (groupId?: number) => boolean;
  isItemDisabled: (itemId: number) => boolean;
  isQuestionAllItemsDisabled: (items: number[]) => boolean;
}

function findAllKeysByValue(data: Record<string, any>, targetValue: number): string[] {
  return Object.entries(data)
    .filter(([_, value]) => value === String(targetValue))
    .map(([key, _]) => key);
}

export const computeRuleState = (
  allQuestions: QuestionnaireQuestionDto[],
  collectedData: Record<string, any>,
  formValues: Record<string, any>,
): RuleState => {
  const state: RuleState = {
    targetsOfVisibleQuestions: new Array<{
      questionId: number;
      itemId: number;
      targetId: number;
      externalId: string | null;
      isTargeted: boolean;
    }>(),
    targetsOfVisibleGroups: new Array<{ groupId: number; isTargeted: boolean }>(),
    targetsOfDisabledItems: new Array<{ itemId: number; isTargeted: boolean }>(),
    isQuestionVisible: function (targetId: number, externalId: string | null) {
      const isTargetedAtAll = this.targetsOfVisibleQuestions.some((t) => t.targetId === targetId);

      if (!isTargetedAtAll) return true;

      return this.targetsOfVisibleQuestions.some((t) => t.targetId === targetId && t.externalId === externalId && t.isTargeted);
    },
    isGroupVisible: function (groupId?: number) {
      const isTargetedAtAll = this.targetsOfVisibleGroups.some((t) => t.groupId === groupId);
      if (!isTargetedAtAll) return true;

      return this.targetsOfVisibleGroups.some((target) => target.groupId === groupId && target.isTargeted);
    },
    isItemDisabled: function (itemId: number) {
      return this.targetsOfDisabledItems.some((target) => target.itemId === itemId && target.isTargeted);
    },
    isQuestionAllItemsDisabled: function (items: number[]) {
      return (
        items.length > 0 && items.every((id) => this.targetsOfDisabledItems.some((target) => target.itemId === id && target.isTargeted))
      );
    },
  };

  const allCombinedValues = { ...collectedData, ...formValues };

  for (const q of allQuestions) {
    if (!q.questionnaireQuestionItems) continue;

    for (const item of q.questionnaireQuestionItems) {
      if (!item.itemAssignments) continue;

      for (const assignment of item.itemAssignments) {
        const selectedFieldNames = findAllKeysByValue(allCombinedValues, item.questionnaireQuestionItemID);
        const isFieldNameSelected = selectedFieldNames.length > 0;

        switch (assignment.type) {
          case "Visible":
            if (assignment.childQuestionID) {
              if (isFieldNameSelected) {
                for (const existingFieldName of selectedFieldNames) {
                  const hasExternalId = existingFieldName.split("_").length > 1;
                  state.targetsOfVisibleQuestions.push({
                    targetId: assignment.childQuestionID,
                    questionId: q.questionnaireQuestionID,
                    itemId: item.questionnaireQuestionItemID,
                    externalId: hasExternalId ? existingFieldName.split("_")[1] : null,
                    isTargeted: true,
                  });
                }
              } else {
                state.targetsOfVisibleQuestions.push({
                  targetId: assignment.childQuestionID,
                  questionId: q.questionnaireQuestionID,
                  itemId: item.questionnaireQuestionItemID,
                  externalId: null,
                  isTargeted: false,
                });
              }
            }
            if (assignment.childGroupID) {
              state.targetsOfVisibleGroups.push({ groupId: assignment.childGroupID, isTargeted: isFieldNameSelected });
            }
            break;
          case "Disabled":
            if (assignment.childQuestionItemID) {
              state.targetsOfDisabledItems.push({
                itemId: assignment.childQuestionItemID,
                isTargeted: isFieldNameSelected,
              });
            }
            break;
          case "Excluded":
            // Not implemented yet (only for BE evaluation)
            break;
          case "Optional":
            // Not implemented yet
            break;
          case "Inconsistent":
            // Not implemented yet
            break;
          case "Related":
            // Not implemented (Relations between PPP and IP)
            break;
        }
      }
    }
  }

  return state;
};

export const useRuleEngine = (allQuestions: QuestionnaireQuestionDto[], collectedData: Record<string, any> = {}) => {
  const { control } = useFormContext();
  const formValues = useWatch({ control }) || {};

  return useMemo(() => computeRuleState(allQuestions, collectedData, formValues), [allQuestions, collectedData, formValues]);
};
